import React, { useRef, useMemo } from 'react'
import JoditEditor from 'jodit-react'
import { Box } from '@mui/material'

/**
 * Professional HTML Signature Editor.
 * Jodit is required for complex table-based Outlook signatures because
 * standard editors like Quill strip out table tags and inline styles.
 */
const SignatureEditor = ({ value, onChange, height = 300, readOnly = false }) => {
  const editorRef = useRef(null)

  const config = useMemo(() => ({
    readonly: readOnly,
    height,
    minHeight: height,
    toolbarSticky: false,
    showCharsCounter: false,
    showWordsCounter: false,
    showXPathInStatusbar: false,
    allowResizeX: false,
    allowResizeY: false,
    
    cleanHTML: {
      cleanOnPaste: false,
      removeEmptyNodes: false,
      replaceNBSP: false,
      fillEmptyParagraph: false,
    },
    // Explicitly allow all tags and attributes for complex signatures
    allowTags: '*',
    allowAttributes: '*',
    beautyHTML: true,
    
    // Outlook style options
    buttons: [
      'source', '|', 
      'bold', 'italic', 'underline', 'strikethrough', '|',
      'font', 'fontsize', 'brush', 'paragraph', '|',
      'ul', 'ol', 'align', '|',
      'link', 'table', 'image', '|',
      'undo', 'redo', 'eraser', 'fullsize'
    ],
    
    fontValues: {
      Arial: 'Arial, Helvetica, sans-serif',
      'Times New Roman': 'Times New Roman, serif',
      'Courier New': 'Courier New, monospace',
      'Open Sans': "'Open Sans', sans-serif",
      Roboto: "'Roboto', sans-serif",
      Calibri: 'Calibri, sans-serif',
      Verdana: 'Verdana, sans-serif',
      Georgia: 'Georgia, serif',
    },
    
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    defaultActionOnPaste: 'insert_as_html',
    
    // Use iframe to isolate the signature from global CSS interference
    iframe: true,
    iframeStyle: 'html{margin:0;padding:0;} body{font-family:Arial, sans-serif;font-size:14px;line-height:normal;margin:0;padding:15px;min-width:550px;background:#fff;overflow-x:auto;} table{border-collapse:collapse;}',

    // Prevent Jodit from adding its own table styles
    useAceEditor: false,
    processTables: false,
    
    style: {
      font: '14px Arial, sans-serif',
      background: '#ffffff',
      width: '100%',
    }
  }), [height, readOnly])

  return (
    <Box sx={{
      '& .jodit-container': {
        borderRadius: '8px !important',
        border: '1px solid #e0e6f7 !important',
      },
      '& .jodit-toolbar__box': {
        backgroundColor: '#f8fafc !important',
        borderBottom: '1px solid #e2e8f0 !important',
      }
    }}>
      <JoditEditor
        ref={editorRef}
        value={value || ''}
        config={config}
        onBlur={(newContent) => onChange(newContent)}
        onChange={() => {}}
      />
    </Box>
  )
}

export default SignatureEditor
